import LockIcon from '@mui/icons-material/Lock';
import LogoutIcon from '@mui/icons-material/Logout';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { signOut } from 'aws-amplify/auth';
import { motion } from 'framer-motion';
import React from 'react';

import type { AccountLockoutStatus } from '@/api/accountLockout';
import { createCustomerPortalSession } from '@/services/subscriptionApi';
import theme from '@/theme';

interface AccountLockedPageProps {
  lockoutInfo: AccountLockoutStatus;
}

export default function AccountLockedPage({ lockoutInfo }: AccountLockedPageProps) {
  const [portalLoading, setPortalLoading] = React.useState(false);
  const [logoutDialogOpen, setLogoutDialogOpen] = React.useState(false);

  const canSelfUnlock = lockoutInfo.can_self_unlock === true;

  const handleGoToPayment = async () => {
    setPortalLoading(true);
    try {
      const response = await createCustomerPortalSession();
      window.location.href = response.portal_url;
    } catch (error) {
      console.error('Failed to open payment portal:', error);
      alert('Failed to open payment portal. Please contact support.');
    } finally {
      setPortalLoading(false);
    }
  };

  const formatAmount = (cents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(cents / 100);
  };

  const handleLogoutClick = () => {
    setLogoutDialogOpen(true);
  };

  const handleLogoutCancel = () => {
    setLogoutDialogOpen(false);
  };

  const handleLogoutConfirm = async () => {
    setLogoutDialogOpen(false);
    try {
      await signOut();
      window.location.assign('/');
    } catch (error) {
      console.error('Failed to sign out:', error);
    }
  };

  return (
    <>
      <Box
        component={motion.div}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          textAlign: 'center',
          px: 3,
          backgroundColor: theme.palette.primary.main,
          color: 'white',
          position: 'relative',
        }}
      >
        {/* Sign Out Button - Top Right */}
        <IconButton
          onClick={handleLogoutClick}
          sx={{
            position: 'absolute',
            top: 24,
            right: 24,
            color: 'white',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            },
          }}
          aria-label="sign out"
        >
          <LogoutIcon />
        </IconButton>

        {/* Logo */}
        <img src="/logo-light.png" alt="SimpleSUB Logo" style={{ width: 400, marginBottom: 60 }} />

        {/* Lock Icon */}
        <LockIcon sx={{ fontSize: 80, mb: 3 }} />

        <Typography variant="h4" component="h1" gutterBottom>
          Account Access Restricted
        </Typography>

        {canSelfUnlock ? (
          <Stack spacing={3} alignItems="center" sx={{ maxWidth: 500 }}>
            {/* Service Fee Delinquent - Show payment option */}
            <Alert severity="error" sx={{ width: '100%' }}>
              Your SimpleSUB service subscription payment is past due. Please update your payment
              information to restore access to your account.
            </Alert>

            {lockoutInfo.amount_due_cents != null && (
              <Box
                sx={{
                  width: '100%',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  padding: 2,
                  borderRadius: 1,
                  textAlign: 'center',
                }}
              >
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Outstanding Stripe Balance:
                </Typography>
                <Typography variant="h5" fontWeight="bold">
                  {formatAmount(lockoutInfo.amount_due_cents)}
                </Typography>
              </Box>
            )}

            <LoadingButton
              variant="contained"
              size="large"
              fullWidth
              onClick={handleGoToPayment}
              loading={portalLoading}
              disabled={portalLoading}
              sx={{
                backgroundColor: 'white',
                color: theme.palette.primary.main,
                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.9)' },
              }}
            >
              {portalLoading ? 'Loading...' : 'Update Payment & Restore Access'}
            </LoadingButton>

            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Once payment is processed, your access will be automatically restored.
            </Typography>
          </Stack>
        ) : (
          <Stack spacing={3} alignItems="center" sx={{ maxWidth: 500 }}>
            {/* Other Lockout - No payment option */}
            <Alert severity="warning" sx={{ width: '100%' }}>
              Your account has been restricted. Please contact support for assistance.
            </Alert>
          </Stack>
        )}

        {/* Support info on BOTH pages */}
        <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid rgba(255, 255, 255, 0.3)', maxWidth: 500 }}>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            Questions or need help?{' '}
            <a href="mailto:support@simplesubwater.com" style={{ color: 'white', fontWeight: 600 }}>
              support@simplesubwater.com
            </a>
          </Typography>
        </Box>
      </Box>

      {/* Logout Confirmation Dialog */}
      <Dialog
        open={logoutDialogOpen}
        onClose={handleLogoutCancel}
        PaperProps={{
          sx: {
            borderRadius: 2,
            minWidth: 400,
          },
        }}
      >
        <DialogTitle>Sign Out</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to sign out?</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleLogoutCancel} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleLogoutConfirm} variant="contained" color="primary">
            Sign Out
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
